import React, { useEffect } from "react";
import styled from "styled-components/macro";

import { Helmet } from "react-helmet-async";

import {
	Breadcrumbs as MuiBreadcrumbs,
	Button,
	Card as MuiCard,
	CardContent,
	Divider as MuiDivider,
	Fade,
	Grid,
	IconButton,
	Link,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from "@material-ui/core";

import { spacing } from "@material-ui/system";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store/reducer";
import { IProjectsState } from "../../redux/reducers/projectsReducer";
import { Alert, Skeleton } from "@material-ui/lab";
import {
	NavLink,
	useHistory,
	useLocation,
	useParams,
} from "react-router-dom";
import { ChevronRight, Edit, Trash2 } from "react-feather";
import {
	Add as AddIcon,
	PieChart,
} from "@material-ui/icons";
import { useStyles } from "../../theme/useStyles";
import { useTable } from "../../hooks/useTable";
import { timesStampToDateFormatted } from "../../utils/utils";
import { getOneProject } from "../../redux/actions/projectsActions";
import { useNewEditDialog } from "../../hooks/useNewEditDialog";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { DialogNewEdit } from "./components/DialogNewEdit";
import { IRevisionsState } from "../../redux/reducers/revisionsReducer";
import {
	addRevision,
	deleteRevision,
	getRevisions,
	setSelectedRevision,
	updateRevision,
} from "../../redux/actions/revisionsActions";
import { TableSkeleton } from "../../components/TableSkeleton";
import { NewConfirmDialog } from "../../components/NewConfirmDialog";
import {
	TABLE_LIMITS,
	TABLE_LIMIT_DEFAULT,
} from "../../constants";
import { IBusinessState } from "../../redux/reducers/businessReducer";
import { getBusiness } from "../../redux/actions/businessActions";
import { DropdownMenu } from "../../components/Generic/DropdownMenu";
import { Opener } from "../../components/CheckPro/Opener";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

interface Props {
	array: any[];
	isLoading: boolean;
	openEditDialog: (selected: any) => void;
	openConfirmDialog: (selected: any) => any;
	project: any;
}

const ContentCard: React.FC<Props> = ({
	array,
	isLoading,
	project,
	openEditDialog,
	openConfirmDialog,
}) => {
	const classes = useStyles();
	const history = useHistory();
	const dispatch = useDispatch();

	const {
		limit,
		handleLimitChange,
		handlePageChange,
		page,
	} = useTable({
		limit: TABLE_LIMIT_DEFAULT || 5,
	});

	const theme = useTheme();

	return (
		<Card mb={6}>
			<CardContent>
				<TableContainer className={classes.tableContainer}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>Nombre</TableCell>
								<TableCell>Listados</TableCell>
								<TableCell>Encargado</TableCell>
								<TableCell>Cargo</TableCell>
								<TableCell>Fecha de Creación</TableCell>
								<TableCell align="center">
									Acciones
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{!isLoading ? (
								array
									.slice(page * limit, page * limit + limit)
									.map((data: any, i: any) => (
										<Fade key={i} in={true}>
											<TableRow
												hover
												className={classes.styledRow}
											>
												<TableCell>{data.Nombre}</TableCell>
												<TableCell>
													{typeof data.Carpetas === "string"
														? data?.Carpetas?.replace(
																"(",
																""
														  )?.replace(")", "")
														: data.Carpetas}
												</TableCell>
												<TableCell>
													{data.Responsable.NombreCompleto}
												</TableCell>
												<TableCell>
													{data.Responsable?.Cargo}
												</TableCell>
												<TableCell>
													{timesStampToDateFormatted(
														data.FechaCreacion
													)}
												</TableCell>
												<TableCell
													style={{ whiteSpace: "nowrap" }}
													align="center"
												>
													<DropdownMenu
														Opener={Opener}
														options={[
															{
																label: "Editar",
																icon: (
																	<Edit
																		size={22}
																		color={
																			theme.palette.primary
																				.main
																		}
																	/>
																),
																onClick: () =>
																	openEditDialog(data),
															},
															{
																label: "Eliminar",
																icon: (
																	<Trash2
																		color="red"
																		size={22}
																	/>
																),
																onClick: () =>
																	openConfirmDialog(data),
															},
															{
																label: "Estadísticas",
																icon: (
																	<PieChart color="primary" />
																),
																onClick: () =>
																	history.push(
																		`/administrador/estadisticas/${project.id}/${data.id}`
																	),
															},
														]}
													/>

													<Tooltip title="Listados">
														<IconButton
															size="small"
															color="primary"
															onClick={() => {
																dispatch(
																	setSelectedRevision(data)
																);
																history.push(
																	`revisiones/${data.id}/listados`
																);
															}}
														>
															<ChevronRight />
														</IconButton>
													</Tooltip>
												</TableCell>
											</TableRow>
										</Fade>
									))
							) : (
								<TableSkeleton colSpan={5} limit={limit} />
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={array.length}
					onChangePage={handlePageChange as any}
					onChangeRowsPerPage={handleLimitChange}
					page={page}
					rowsPerPage={limit}
					rowsPerPageOptions={TABLE_LIMITS}
				/>
			</CardContent>
		</Card>
	);
};

export const RevisionsPage = () => {
	const { pathname } = useLocation();
	const { businessId, projectId } = useParams<any>();
	const dispatch = useDispatch();

	const {
		edit: { selectedBusiness },
	} = useSelector<RootState, IBusinessState>(
		(state) => state.businessReducer
	);

	const {
		revisions: {
			isLoading: projectIsLoading,
			selectedProject,
			error,
		},
	} = useSelector<RootState, IProjectsState>(
		(state) => state.projectsReducer
	);

	const {
		revisions,
		isLoading: revisionIsLoading,
		updateLoading,
		totalDocs,
	} = useSelector<RootState, IRevisionsState>(
		(state) => state.revisionsReducer
	);

	useEffect(() => {
		if (selectedProject === null) {
			dispatch(getOneProject(projectId));
		} else if (selectedProject) {
			dispatch(getRevisions(selectedProject));
		}
	}, [selectedProject, dispatch, projectId]);

	useEffect(() => {
		if (selectedBusiness?.id === businessId) return;
		dispatch(getBusiness(businessId));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [businessId, selectedBusiness?.id]);

	const {
		open,
		openEditDialog,
		openNewDialog,
		closeDialog,
		onAccept,
		action,
		selected,
		setIsLoading: setIsLoadingNewEditDialog,
	} = useNewEditDialog<any>({
		onAccept: (values, action) => {
			if (action === "New") {
				dispatch(addRevision(values));
			} else if (action === "Edit") {
				dispatch(
					updateRevision(values, selected, selected.id)
				);
			}
		},
	});

	const {
		open: openConfirmDialog,
		setIsLoading: setIsLoadingConfirmDialog,
		dialogConfirmHandleClose,
		dialogConfirmHandleConfirm,
		openDialog,
		selected: confirmDialogSelected,
	} = useConfirmDialog({
		onConfirm: (selected) => {
			dispatch(
				deleteRevision(selected.id, selectedProject!.id)
			);
		},
	});

	useEffect(() => {
		setIsLoadingNewEditDialog(updateLoading);
		setIsLoadingConfirmDialog(updateLoading);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateLoading]);

	return (
		<React.Fragment>
			<Helmet title="Revisiones" />

			<Grid justify="space-between" container>
				<Grid item>
					{projectIsLoading && !selectedProject ? (
						<Skeleton variant="text" />
					) : (
						<Typography
							variant="h3"
							gutterBottom
							display="inline"
						>
							{`${
								selectedBusiness?.Nombre ?? "Empresa"
							} / ${
								selectedProject?.NombreProyecto ??
								"Proyecto"
							}`}
						</Typography>
					)}

					{pathname.startsWith(
						"/administrador/empresas/"
					) ? (
						<Breadcrumbs aria-label="Breadcrumb" mt={2}>
							<Link
								component={NavLink}
								exact
								to="/administrador/empresas"
							>
								Empresas
							</Link>
							<Link
								component={NavLink}
								exact
								to={`/administrador/empresas/${businessId}/proyectos`}
							>
								Proyectos
							</Link>
							<Typography>Revisiones</Typography>
						</Breadcrumbs>
					) : (
						<Breadcrumbs aria-label="Breadcrumb" mt={2}>
							<Link
								component={NavLink}
								exact
								to="/mantenedores/revisiones/proyectos"
							>
								Proyectos
							</Link>
							<Typography>Revisiones</Typography>
						</Breadcrumbs>
					)}
				</Grid>
				<Grid item>
					<div>
						<Button
							variant="contained"
							color="primary"
							onClick={openNewDialog}
						>
							<AddIcon />
							Nueva Revisión
						</Button>
					</div>
				</Grid>
			</Grid>

			<Divider my={6} />

			<Grid container spacing={6}>
				<Grid item xs={12}>
					<ContentCard
						project={selectedProject}
						array={revisions}
						isLoading={revisionIsLoading}
						openEditDialog={openEditDialog}
						openConfirmDialog={openDialog}
					/>
				</Grid>
			</Grid>

			{selectedProject && (
				<React.Fragment>
					<DialogNewEdit
						open={open}
						onClose={closeDialog}
						onAccept={onAccept}
						isLoading={updateLoading}
						action={action}
						title="Revisión"
						selected={selected}
						project={selectedProject}
						error={error ? error : null}
					/>
					<NewConfirmDialog
						open={openConfirmDialog}
						title={`¿Desea borrar la revisión ${confirmDialogSelected?.Nombre}?`}
						content={
							<Alert severity="warning">
								Se borraran los Listado, y Observaciones que
								contenía esta Revisión
							</Alert>
						}
						onClose={dialogConfirmHandleClose}
						isLoading={updateLoading}
						onConfirm={dialogConfirmHandleConfirm}
						onConfirmText="Borrar"
					/>
				</React.Fragment>
			)}
		</React.Fragment>
	);
};
